/**
 * ppm → percentual pt-BR ("9,3%", "90%", "0%"). 1% = 10.000 ppm. Tira só zeros DEPOIS da vírgula:
 * a versão antiga apagava zeros significativos e mostrava "100%" como "1%".
 */
export function percentualPpm(ppm: number, casas = 2): string {
  const s = (ppm / 10_000).toFixed(casas).replace('.', ',')
  return `${s.includes(',') ? s.replace(/0+$/, '').replace(/,$/, '') : s}%`
}

/**
 * Número inteiro em unidades mínimas → texto pt-BR com casas fixas ("1.234,56", "17,00").
 * `casasEscala`: quantas casas a unidade mínima tem (centavos = 2, ppm como % = 4, FAP em ppm = 6).
 * Mostra ao menos `casas` e no máximo `casasMax` casas (arredonda meio para cima), sem float.
 */
export function formatarDecimal(valor: number, casasEscala: number, casas = 2, casasMax = casas): string {
  const neg = valor < 0
  let abs = BigInt(Math.abs(Math.trunc(valor)))
  const corte = casasEscala - Math.min(casasMax, casasEscala)
  if (corte > 0) {
    const d = 10n ** BigInt(corte)
    abs = (abs + d / 2n) / d
  }
  const escala = Math.min(casasMax, casasEscala)
  const texto = abs.toString().padStart(escala + 1, '0')
  const inteira = texto.slice(0, texto.length - escala).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  let fracao = escala ? texto.slice(texto.length - escala) : ''
  while (fracao.length > casas && fracao.endsWith('0')) fracao = fracao.slice(0, -1)
  fracao = fracao.padEnd(casas, '0')
  return `${neg && /[1-9]/.test(texto) ? '-' : ''}${inteira}${fracao ? `,${fracao}` : ''}`
}

/**
 * Texto digitado → inteiro em unidades mínimas, sem float. Aceita "1.234,56", "1234,56", "R$ 1.234", "17.5" e "17,5%".
 * Com vírgula, ela é a decimal e os pontos são milhar; só com ponto, é decimal quando não separa grupos de 3 dígitos.
 * Vazio → null; texto inválido → undefined.
 */
export function lerDecimal(texto: string, casasEscala: number): number | null | undefined {
  let t = texto.replace(/R\$|%|\s/g, '')
  if (!t) return null
  const neg = t.startsWith('-') || t.startsWith('−')
  if (neg) t = t.slice(1)
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.')
  else if (/^\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, '')
  const m = /^(\d*)(?:\.(\d*))?$/.exec(t)
  if (!m || (!m[1] && !m[2])) return undefined
  const fracao = (m[2] ?? '').padEnd(casasEscala + 1, '0')
  let v = BigInt(m[1] || '0') * 10n ** BigInt(casasEscala) + BigInt(fracao.slice(0, casasEscala) || '0')
  if (Number(fracao[casasEscala]) >= 5) v += 1n
  const n = Number(v)
  return neg ? -n : n
}
